import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Clock, User } from 'lucide-react';
import { useDarkMode } from '../darkmode/DarkModeContext';

const FALLBACK_NEWS = [
  {
    _id: 'fallback-1',
    title: 'Telnet Home Perluas Jaringan Fiber Optik ke Seluruh Aceh',
    summary: 'Telnet Home terus memperluas infrastruktur jaringan fiber optik untuk menjangkau lebih banyak wilayah di Aceh.',
    image: 'image/berita/fiberoptik.png',
    category: 'Teknologi',
    author: 'Admin Telnet Home',
    slug: 'perluas-jaringan-fiber',
    publishedAt: '2025-01-15T00:00:00Z',
  },
  {
    _id: 'fallback-2',
    title: 'Tips Memilih Paket Internet yang Tepat untuk Keluarga',
    summary: 'Panduan lengkap memilih paket internet sesuai kebutuhan keluarga Anda, dari streaming hingga gaming.',
    image: 'image/berita/fiberoptik.png',
    category: 'Tips',
    author: 'Admin Telnet Home',
    slug: 'tips-memilih-paket',
    publishedAt: '2025-01-10T00:00:00Z',
  },
  {
    _id: 'fallback-3',
    title: 'Keamanan Jaringan: Cara Melindungi WiFi Rumah Anda',
    summary: 'Langkah-langkah praktis untuk mengamankan jaringan WiFi rumah dari ancaman cyber.',
    image: 'image/berita/fiberoptik.png',
    category: 'Keamanan',
    author: 'Admin Telnet Home',
    slug: 'keamanan-jaringan-wifi',
    publishedAt: '2025-01-05T00:00:00Z',
  }
];

const News = () => {
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useDarkMode();

  const API_BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api';

  const getSafeValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.name || value.title || fallback;
    return String(value);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/news?page=1&limit=3&status=published`);
        const result = await response.json();
        if (result.success && result.data?.news?.length > 0) {
          setNewsArticles(result.data.news.slice(0, 3));
        } else {
          setNewsArticles(FALLBACK_NEWS);
        }
      } catch {
        setNewsArticles(FALLBACK_NEWS);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const articles = newsArticles.length > 0 ? newsArticles : FALLBACK_NEWS;

  return (
    <div
      className="py-16 bg-cover bg-center transition-colors duration-300"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      <div className="section-container">
        <div className="text-center mb-10">
          <h2 className={`text-3xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Berita & Informasi
          </h2>
          <p className={`text-sm max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Dapatkan informasi terbaru seputar teknologi, layanan, dan perkembangan Telnet Home
          </p>
          <div className="w-16 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto mt-3 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {articles.map((article) => (
            <div
              key={article._id}
              className={`card-base overflow-hidden cursor-pointer group ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={() => window.location.href = `/news/${article.slug}`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={getSafeValue(article.image, 'image/berita/fiberoptik.png')}
                  alt={getSafeValue(article.title)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = 'image/berita/fiberoptik.png'; }}
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-[#1E9B4C] text-white px-2 py-0.5 rounded-lg text-[10px] font-medium">
                    {getSafeValue(article.category, 'General')}
                  </span>
                </div>
              </div>

              <div className="p-4">
                <div className={`flex items-center gap-3 text-[10px] mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{getSafeValue(article.author, 'Admin')}</span>
                  </div>
                </div>

                <h3 className={`font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[#1E9B4C] transition-colors ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {getSafeValue(article.title)}
                </h3>

                <p className={`text-xs mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {getSafeValue(article.summary)}
                </p>

                <span className="inline-flex items-center gap-1 text-[#1E9B4C] text-xs font-medium">
                  Baca Selengkapnya <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default News;
